/**
 * Operations Center — v7.4 P7
 *
 * Couche "Hospital Command" qui manquait : Occupancy · Beds · Staffing · Resources · Inventory
 *
 * Vue préférée des directeurs d'hôpitaux.
 * Position : à côté des 5 centres existants. Route : /operations
 *
 * Palette : Operations = #10B981 (Emerald)
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { PageHeader, KpiRow } from "@/components/PageHeader";
import { DOMAINS } from "@/lib/design-system";
import {
  Bed, Users, Truck, Package, Wrench, AlertTriangle,
  Building2, Clock, TrendingUp, Activity, Stethoscope,
  Coffee, Calendar
} from "lucide-react";
import { api } from "@/lib/api";
import { safe } from "@/lib/safe-fetch";
import { trackCenter } from "@/lib/track-center";

export const Route = createFileRoute("/operations/")({
  component: OperationsCenter,
});

function OperationsCenter() {
  const [data, setData] = useState<any>({});
  const d = DOMAINS.operations;

  useEffect(() => {
    trackCenter("operations");
    Promise.all([
      safe(() => api.patientStats(), "ops.patientStats"),
      safe(() => api.encounterStats(), "ops.encounterStats"),
      safe(() => api.labStats(), "ops.labStats"),
      safe(() => api.pharmacyStats(), "ops.pharmacyStats"),
    ]).then(([p, e, l, ph]) => setData({ p, e, l, ph }));
  }, []);

  const topKpis = [
    { label: "Lits occupés", value: "62/80", delta: 0, intent: "good" as const, icon: Bed, sparkline: [60, 61, 60, 62, 61, 62, 62] },
    { label: "Staff en poste", value: "47/55", delta: +2, intent: "good" as const, icon: Users, sparkline: [44, 45, 46, 46, 47, 47, 47] },
    { label: "Stocks critiques", value: 3, delta: 0, intent: "bad" as const, icon: Package, sparkline: [3, 3, 3, 3, 3, 3, 3] },
    { label: "Équipements en panne", value: 1, delta: -1, intent: "good" as const, icon: Wrench, sparkline: [2, 2, 2, 2, 1, 1, 1] },
  ];

  // Beds by service
  const beds = [
    { service: "Urgences", total: 12, occupied: 12, occupancy: 100, color: "red" },
    { service: "Médecine", total: 24, occupied: 18, occupancy: 75, color: "amber" },
    { service: "Chirurgie", total: 18, occupied: 14, occupancy: 78, color: "amber" },
    { service: "Réanimation", total: 8, occupied: 7, occupancy: 88, color: "red" },
    { service: "Maternité", total: 10, occupied: 6, occupancy: 60, color: "emerald" },
    { service: "Pédiatrie", total: 8, occupied: 5, occupancy: 62, color: "emerald" },
  ];

  // Staff on shift
  const staff = [
    { role: "Médecins", on: 12, total: 14, color: "sky" },
    { role: "Infirmiers", on: 18, total: 22, color: "emerald" },
    { role: "Aides-soignants", on: 8, total: 10, color: "violet" },
    { role: "Pharmaciens", on: 3, total: 3, color: "amber" },
    { role: "Techniciens labo", on: 4, total: 4, color: "emerald" },
    { role: "Radiologues", on: 2, total: 2, color: "emerald" },
  ];

  // Resources & inventory
  const resources = [
    { label: "Ambulances", total: 3, available: 2, status: "warn" },
    { label: "Salles d'opération", total: 4, available: 1, status: "warn" },
    { label: "IRM", total: 1, available: 0, status: "crit" },
    { label: "Scanner", total: 2, available: 1, status: "ok" },
    { label: "Échographes", total: 3, available: 2, status: "ok" },
  ];

  const inventory = [
    { sku: "Gants nitrile (boîte 100)", stock: 245, min: 50, max: 500, status: "ok" },
    { sku: "Masques FFP2", stock: 1280, min: 200, max: 2000, status: "ok" },
    { sku: "Seringues 10mL", stock: 89, min: 100, max: 1000, status: "warn" },
    { sku: "Compresses stériles", stock: 12, min: 50, max: 500, status: "crit" },
    { sku: "Désinfectant (L)", stock: 45, min: 20, max: 100, status: "ok" },
    { sku: "Cathéters IV", stock: 156, min: 50, max: 300, status: "ok" },
  ];

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 p-4 md:p-6 space-y-4">
      <PageHeader
        title="Operations Center"
        description="Occupancy · Beds · Staffing · Resources · Inventory · Command view pour la direction"
        domain="operations"
        actions={
          <>
            <Badge variant="outline" className="border-emerald-500/30 text-emerald-400">
              <Clock className="h-3 w-3 mr-1" /> Shift jour · 08:00 → 20:00
            </Badge>
            <Button variant="outline" size="sm" asChild>
              <Link to="/admin/administrateur">Clinical</Link>
            </Button>
            <Button size="sm" className="bg-emerald-500 hover:bg-emerald-600 text-slate-900">
              + Action opérationnelle
            </Button>
          </>
        }
      >
        <KpiRow items={topKpis} domain="operations" />
      </PageHeader>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

        {/* Beds by service */}
        <Card className="bg-slate-900/60 border-slate-800">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Bed className={`h-4 w-4 ${d.textClass}`} /> Lits par service
            </CardTitle>
            <p className="text-xs text-slate-500">62 / 80 occupés · 77%</p>
          </CardHeader>
          <CardContent className="space-y-2">
            {beds.map((b) => (
              <div key={b.service} className="bg-slate-950/40 p-3 rounded-lg border border-slate-800">
                <div className="flex items-center justify-between text-sm mb-2">
                  <span className="font-semibold">{b.service}</span>
                  <span className="text-xs text-slate-400">{b.occupied}/{b.total} · {b.occupancy}%</span>
                </div>
                <div className="w-full bg-slate-800 rounded-full h-2 overflow-hidden">
                  <div
                    className={`h-2 ${
                      b.occupancy >= 90 ? "bg-red-500" :
                      b.occupancy >= 75 ? "bg-amber-500" :
                      "bg-emerald-500"
                    }`}
                    style={{ width: `${b.occupancy}%` }}
                  />
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Staff on shift */}
        <Card className="bg-slate-900/60 border-slate-800">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Users className={`h-4 w-4 ${d.textClass}`} /> Personnel en poste
            </CardTitle>
            <p className="text-xs text-slate-500">47 / 55 · shift jour en cours</p>
          </CardHeader>
          <CardContent className="space-y-2">
            {staff.map((s) => (
              <div key={s.role} className="bg-slate-950/40 p-3 rounded-lg border border-slate-800 flex items-center justify-between">
                <div className="text-sm font-semibold">{s.role}</div>
                <div className="flex items-center gap-3">
                  <div className="text-xs text-slate-400">{s.on}/{s.total}</div>
                  <div className={`h-2 w-12 rounded-full overflow-hidden bg-slate-800`}>
                    <div className={`h-2 bg-${s.color}-500`} style={{ width: `${(s.on / s.total) * 100}%` }} />
                  </div>
                </div>
              </div>
            ))}
            <div className="text-xs text-slate-500 mt-2 pt-2 border-t border-slate-800">
              Prochain shift : 20:00 — 08:00 (équipe de nuit, 38 personnes)
            </div>
          </CardContent>
        </Card>

        {/* Resources & equipment */}
        <Card className="bg-slate-900/60 border-slate-800">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Truck className={`h-4 w-4 ${d.textClass}`} /> Équipements
            </CardTitle>
            <p className="text-xs text-slate-500">Disponibilité temps réel</p>
          </CardHeader>
          <CardContent className="grid grid-cols-2 gap-2">
            {resources.map((r) => (
              <div key={r.label} className={`p-3 rounded-lg border ${
                r.status === "crit" ? "bg-red-500/10 border-red-500/30" :
                r.status === "warn" ? "bg-amber-500/10 border-amber-500/30" :
                "bg-slate-950/40 border-slate-800"
              }`}>
                <div className="text-[10px] uppercase tracking-wider text-slate-500">{r.label}</div>
                <div className="text-lg font-bold mt-1">
                  {r.available}<span className="text-xs text-slate-500">/{r.total}</span>
                </div>
                <div className={`text-[10px] mt-1 ${
                  r.status === "crit" ? "text-red-400" :
                  r.status === "warn" ? "text-amber-400" :
                  "text-emerald-400"
                }`}>
                  {r.status === "crit" ? "⚠ Indispo" : r.status === "warn" ? "⚠ Partiel" : "✓ OK"}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Inventory */}
        <Card className="bg-slate-900/60 border-slate-800">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Package className={`h-4 w-4 ${d.textClass}`} /> Inventaire
            </CardTitle>
            <p className="text-xs text-slate-500">Seuils min/max · alertes automatiques</p>
          </CardHeader>
          <CardContent className="space-y-1">
            {inventory.map((i) => (
              <div key={i.sku} className="flex items-center justify-between bg-slate-950/40 p-2 rounded border border-slate-800 text-xs">
                <div className="flex-1 min-w-0">
                  <div className="font-semibold truncate">{i.sku}</div>
                  <div className="text-[10px] text-slate-500">min {i.min} · max {i.max}</div>
                </div>
                <div className="text-right ml-2">
                  <div className={`text-sm font-bold ${
                    i.status === "crit" ? "text-red-400" :
                    i.status === "warn" ? "text-amber-400" :
                    "text-emerald-400"
                  }`}>{i.stock}</div>
                  <div className="w-16 h-1 bg-slate-800 rounded-full overflow-hidden mt-1">
                    <div
                      className={`h-1 ${
                        i.status === "crit" ? "bg-red-500" :
                        i.status === "warn" ? "bg-amber-500" :
                        "bg-emerald-500"
                      }`}
                      style={{ width: `${Math.min(100, (i.stock / i.max) * 100)}%` }}
                    />
                  </div>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      {/* Bottom strip — Quick actions */}
      <Card className="bg-slate-900/60 border-slate-800">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Activity className={`h-4 w-4 ${d.textClass}`} /> Actions rapides
          </CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <Link to="/admin/administrateur/pharma/stocks">
            <div className="bg-slate-950/40 hover:bg-slate-800/60 transition p-3 rounded-lg border border-slate-800">
              <Package className="h-4 w-4 text-emerald-400" />
              <div className="text-xs font-semibold mt-2">Stocks pharma</div>
              <div className="text-[10px] text-slate-500">Seuils + contrôlés</div>
            </div>
          </Link>
          <Link to="/admin/administrateur/lab/resultats">
            <div className="bg-slate-950/40 hover:bg-slate-800/60 transition p-3 rounded-lg border border-slate-800">
              <Stethoscope className="h-4 w-4 text-violet-400" />
              <div className="text-xs font-semibold mt-2">Résultats labo</div>
              <div className="text-[10px] text-slate-500">TAT + critiques</div>
            </div>
          </Link>
          <Link to="/admin/administrateur/consultations">
            <div className="bg-slate-950/40 hover:bg-slate-800/60 transition p-3 rounded-lg border border-slate-800">
              <Calendar className="h-4 w-4 text-sky-400" />
              <div className="text-xs font-semibold mt-2">Agenda</div>
              <div className="text-[10px] text-slate-500">Planifier RDV</div>
            </div>
          </Link>
          <Link to="/admin/administrateur">
            <div className="bg-slate-950/40 hover:bg-slate-800/60 transition p-3 rounded-lg border border-slate-800">
              <Building2 className="h-4 w-4 text-amber-400" />
              <div className="text-xs font-semibold mt-2">Clinical OpC</div>
              <div className="text-[10px] text-slate-500">Vue médecin</div>
            </div>
          </Link>
        </CardContent>
      </Card>
    </div>
  );
}